import { useSuspenseQuery } from '@apollo/client/react';
import { PencilLine } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Fragment, useMemo, useRef } from 'react';

import {
  AssetContentTypeEnum,
  SuggestedChangeChangeTypeEnum,
  EscrowStatusEnum,
  SuggestedChangeStatusEnum,
  EscrowPartyTypeEnum,
} from 'src/graphql/schema';
import { SuggestedChangesDocument } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { Button } from '@/components/ui/button';
import { RequestChangesTooltip } from 'src/containers/SoftwareEscrow/RequestChanges';
import { AssetsContextProvider } from 'src/containers/Deposits';
import { ReviewChangesDialog } from 'src/containers/SoftwareEscrow/RequestChanges/Deposits';

import { AssetsBlock, AssetsLabelingBlock, AssetsSummary } from '.';

import { DepositsContextProvider, useDepositsContext } from '../model';

import type { ISuggestedChangesQuery } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';

const RequestChangesActions = ({
  suggestedChange,
}: {
  suggestedChange?: ISuggestedChangesQuery['suggestedChanges']['nodes'][0];
}) => {
  const navigate = useNavigate();
  const {
    softwareEscrow: { id, policy, currentCompanyRole },
  } = useSoftwareEscrow();

  const onRequestChanges = () => navigate(`/software-escrows/${id}/request-changes/deposits`);

  const actions = useMemo(
    () => [
      {
        id: 'review',
        component: <ReviewChangesDialog suggestedChangeId={suggestedChange?.id} />,
        condition: suggestedChange?.policy.review,
      },
      {
        id: 'pendingRequestChanges',
        component: <RequestChangesTooltip />,
        condition:
          suggestedChange?.status === SuggestedChangeStatusEnum.Pending &&
          currentCompanyRole === EscrowPartyTypeEnum.Depositor,
      },
      {
        id: 'createDepositSuggestedChange',
        component: (
          <Button variant="outline" onClick={onRequestChanges}>
            <PencilLine />
            Request changes
          </Button>
        ),
        condition: policy.createDepositSuggestedChange,
      },
    ],
    [suggestedChange, policy, currentCompanyRole, onRequestChanges],
  );

  const actionsFiltered = actions.filter((action) => action.condition);

  if (!actionsFiltered.length) return null;

  return (
    <div className="flex gap-3">
      {actionsFiltered.map(({ id, component }) => (
        <Fragment key={id}>{component}</Fragment>
      ))}
    </div>
  );
};

const Container = () => {
  const {
    softwareEscrow: { id, status },
  } = useSoftwareEscrow();
  const { sourceCodeAssetsQueryRef, documentationAssetsQueryRef, dataAssetsQueryRef, unlabeledAssetsQueryRef } =
    useDepositsContext();
  const { data } = useSuspenseQuery(SuggestedChangesDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      escrowId: id,
      changeType: SuggestedChangeChangeTypeEnum.DepositSuggestedChange,
    },
    skip: status !== EscrowStatusEnum.Active,
  });

  const sectionRefs = {
    unlabeled: useRef<HTMLDivElement>(null),
    sourceCode: useRef<HTMLDivElement>(null),
    documentation: useRef<HTMLDivElement>(null),
    data: useRef<HTMLDivElement>(null),
  };

  return (
    <Fragment>
      <div className="flex justify-between items-center gap-2">
        <div className="space-y-1">
          <div className="text-base font-semibold text-gray-700">Deposits</div>
          <p className="text-sm text-gray-500">
            All assets associated to your escrow. Assets connected through integrations are checked for updates daily.
          </p>
        </div>
        <RequestChangesActions suggestedChange={data?.suggestedChanges.nodes[0]} />
      </div>
      <AssetsSummary sectionRefs={sectionRefs} />
      <AssetsLabelingBlock
        title="Unlabeled assets"
        description="The assets listed below are connected to your escrow but don’t have an assigned asset type. Please review and label each asset appropriately."
        queryRef={unlabeledAssetsQueryRef}
        ref={sectionRefs.unlabeled}
      />
      <AssetsContextProvider
        contentType={AssetContentTypeEnum.SourceCode}
        queryRef={sourceCodeAssetsQueryRef}
        escrowId={id}
      >
        <AssetsBlock
          title="Source code"
          description="The core source code, including all scripts, libraries, and dependencies required to build and run the application."
          ref={sectionRefs.sourceCode}
        />
      </AssetsContextProvider>
      <AssetsContextProvider
        contentType={AssetContentTypeEnum.Documentation}
        queryRef={documentationAssetsQueryRef}
        escrowId={id}
      >
        <AssetsBlock
          title="Documentation"
          description="Technical and operational guides, including API references, setup instructions, system architecture, and any relevant notes required to transfer, operate, and maintain the application."
          ref={sectionRefs.documentation}
        />
      </AssetsContextProvider>
      <AssetsContextProvider contentType={AssetContentTypeEnum.Data} queryRef={dataAssetsQueryRef} escrowId={id}>
        <AssetsBlock
          title="Data"
          description="Essential datasets, configurations, and database exports necessary for the application’s functionality."
          ref={sectionRefs.data}
        />
      </AssetsContextProvider>
    </Fragment>
  );
};

const EscrowDepositsContainer = () => {
  return (
    <DepositsContextProvider>
      <Container />
    </DepositsContextProvider>
  );
};

export default EscrowDepositsContainer;
